import { useState, useRef, useEffect } from 'react'
import { Settings2, Check, RotateCcw } from 'lucide-react'
import { clsx } from 'clsx'
import { AVAILABLE_COLUMNS } from '../types'
import { useTargetAccountsStore } from '../store'
import { useAuthStore } from '@store/authStore'

export function ColumnPicker() {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { visibleColumns, toggleColumn, resetColumns } = useTargetAccountsStore()
  const { user } = useAuthStore()

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Count how many optional columns are visible
  const defaultColumns = AVAILABLE_COLUMNS.filter((c) => c.defaultVisible)
  const optionalColumns = AVAILABLE_COLUMNS.filter((c) => !c.defaultVisible)
  const visibleOptionalCount = optionalColumns.filter((c) => visibleColumns.includes(c.id)).length

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors',
          isOpen
            ? 'bg-[var(--bg-tertiary)] border-primary text-primary'
            : 'bg-[var(--bg-secondary)] border-[var(--border-color)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]'
        )}
      >
        <Settings2 size={16} />
        <span>Columns</span>
        {visibleOptionalCount > 0 && (
          <span className="bg-primary text-white text-xs px-1.5 py-0.5 rounded-full">
            +{visibleOptionalCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-lg z-50">
          <div className="p-3 border-b border-[var(--border-color)]">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-[var(--text-primary)]">Show Columns</span>
              <button
                onClick={() => resetColumns(user?.email, user?.email)}
                className="flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-primary transition-colors"
              >
                <RotateCcw size={12} />
                Reset
              </button>
            </div>
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {/* Default columns section */}
            <div className="p-2">
              <div className="px-2 py-1 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide">
                Default Columns
              </div>
              {defaultColumns.map((column) => (
                <ColumnItem
                  key={column.id}
                  column={column}
                  isVisible={visibleColumns.includes(column.id)}
                  onToggle={() => toggleColumn(column.id, user?.email, user?.email)}
                  disabled={column.id === 'name'} // Can't hide company name
                />
              ))}
            </div>

            {/* Additional columns section */}
            <div className="p-2 border-t border-[var(--border-color)]">
              <div className="px-2 py-1 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide">
                Additional Columns
              </div>
              {optionalColumns.map((column) => (
                <ColumnItem
                  key={column.id}
                  column={column}
                  isVisible={visibleColumns.includes(column.id)}
                  onToggle={() => toggleColumn(column.id, user?.email, user?.email)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

interface ColumnItemProps {
  column: (typeof AVAILABLE_COLUMNS)[number]
  isVisible: boolean
  onToggle: () => void
  disabled?: boolean
}

function ColumnItem({ column, isVisible, onToggle, disabled }: ColumnItemProps) {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={clsx(
        'w-full flex items-center gap-3 px-2 py-2 rounded-md text-left transition-colors',
        disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:bg-[var(--bg-tertiary)] cursor-pointer'
      )}
    >
      <div
        className={clsx(
          'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
          isVisible
            ? 'bg-primary border-primary'
            : 'border-[var(--border-color)] bg-transparent'
        )}
      >
        {isVisible && <Check size={12} className="text-white" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[var(--text-primary)]">{column.label}</div>
        {column.description && (
          <div className="text-xs text-[var(--text-muted)] truncate">{column.description}</div>
        )}
      </div>
    </button>
  )
}
